import React, { useState, useEffect } from 'react';
import { downloadCsv } from '../common/sharedFunctions';
import MaterialTable from "material-table";
import CircularLoading from "../components/CircularLoading";
import { useSelector, useDispatch } from "react-redux";
import { api } from 'common';
import { useTranslation } from "react-i18next";
import moment from 'moment/min/moment-with-locales';
import { colors } from '../components/Theme/WebTheme';
import { WEB_SECONDORY_COLOR, FONT_FAMILY } from "../common/sharedFunctions";
import { ThemeProvider } from '@mui/material/styles';
import theme from "styles/tableStyle";
import TableStyle from '../components/Table/Style';
import localization from '../components/Table/Localization';
import { TextField, Typography } from '@mui/material';

const B2BPartners = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const settings = useSelector(state => state.settingsdata.settings);
  const staticusers = useSelector(state => state.usersdata.staticusers);
  const bookinglistdata = useSelector(state => state.bookinglistdata);
  const { fetchUsersOnce, fetchBookings } = api;
  const dispatch = useDispatch();
  const SECONDORY_COLOR = WEB_SECONDORY_COLOR(settings?.appCat);

  const [month, setMonth] = useState(moment().format('YYYY-MM'));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  function formatAmount(value) {
    const number = parseFloat(value || 0);
    return number.toLocaleString("en-US", {
      minimumFractionDigits: settings ? settings.decimal : 2,
      maximumFractionDigits: settings ? settings.decimal : 2
    });
  }

  const symbolize = (value) =>
    settings && settings.swipe_symbol
      ? formatAmount(value) + " " + (settings ? settings.symbol : '')
      : (settings ? settings.symbol : '') + " " + formatAmount(value);

  useEffect(() => {
    dispatch(fetchBookings());
    dispatch(fetchUsersOnce());
  }, [dispatch, fetchBookings, fetchUsersOnce]);

  const bookingsForCompany = (companyId) => {
    const all = (bookinglistdata && bookinglistdata.bookings) ? bookinglistdata.bookings : [];
    const start = moment(month, 'YYYY-MM').startOf('month').valueOf();
    const end = moment(month, 'YYYY-MM').endOf('month').valueOf();
    return all.filter(b => {
      if (b.fleetadmin !== companyId) return false;
      const dt = b.tripdate || b.bookingDate;
      if (!dt) return true;
      const ts = typeof dt === 'number' ? dt : new Date(dt).getTime();
      return ts >= start && ts <= end;
    });
  };

  const amountOf = (b) => parseFloat(b.trip_cost || b.estimate || 0);

  useEffect(() => {
    if (staticusers) {
      const companies = staticusers.filter(u => u.usertype === 'fleetadmin');
      const rows = companies.map(c => {
        const bks = bookingsForCompany(c.id);
        const billed = bks.reduce((s, b) => s + amountOf(b), 0);
        const commission = bks.reduce((s, b) => s + parseFloat(b.fleetCommission || 0), 0);
        const completed = bks.filter(b => b.status === 'COMPLETE' || b.status === 'PAID').length;
        return {
          id: c.id,
          company: [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || c.id,
          email: c.email || '',
          mobile: c.mobile || '',
          bookings: bks.length,
          completed,
          billed,
          commission,
          _rows: bks
        };
      });
      setData(rows);
      setLoading(false);
    } else {
      setData([]);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staticusers, bookinglistdata, month]);

  const exportStatement = (row) => {
    const header = ['booking_id', 'reference', 'date', 'status', 'pickup', 'drop', 'car_type', 'amount', 'fleet_commission'];
    const lines = (row._rows || []).map(b => [
      b.id,
      b.reference || '',
      b.tripdate ? new Date(b.tripdate).toLocaleString() : '',
      b.status || '',
      b.pickup && b.pickup.add ? `"${b.pickup.add.replace(/"/g, "'")}"` : '',
      b.drop && b.drop.add ? `"${b.drop.add.replace(/"/g, "'")}"` : '',
      b.carType || '',
      amountOf(b).toFixed(settings ? settings.decimal : 2),
      parseFloat(b.fleetCommission || 0).toFixed(settings ? settings.decimal : 2)
    ]);
    const totalRow = ['', '', '', '', '', '', 'TOTAL', row.billed.toFixed(settings ? settings.decimal : 2), row.commission.toFixed(settings ? settings.decimal : 2)];
    const csv = [header, ...lines, totalRow].map(e => e.join(',')).join('\n');
    downloadCsv(csv, `estado_b2b_${(row.company || row.id).replace(/\s+/g, '_')}_${month}.csv`);
  };

  const columns = [
    { title: t('company', { defaultValue: 'Empresa' }), field: 'company' },
    { title: t('email', { defaultValue: 'Correo' }), field: 'email' },
    { title: t('mobile', { defaultValue: 'Móvil' }), field: 'mobile' },
    { title: t('bookings', { defaultValue: 'Servicios' }), field: 'bookings', type: 'numeric' },
    { title: t('completed', { defaultValue: 'Completados' }), field: 'completed', type: 'numeric' },
    { title: t('total_billed', { defaultValue: 'Total facturado' }), field: 'billed', render: (r) => symbolize(r.billed) },
    { title: t('fleet_commission', { defaultValue: 'Comisión' }), field: 'commission', render: (r) => symbolize(r.commission) },
  ];

  const totalCompanies = data.length;
  const totalBookings = data.reduce((s, r) => s + r.bookings, 0);
  const totalBilled = data.reduce((s, r) => s + r.billed, 0);

  return (
    loading ? <CircularLoading /> :
      <ThemeProvider theme={theme}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', margin: '0 0 14px 0', direction: isRTL === "rtl" ? "rtl" : "ltr" }}>
          <div style={{ background: '#000000', color: '#fff', borderRadius: 12, padding: '14px 20px', minWidth: 200 }}>
            <div style={{ fontSize: 13, opacity: 0.8 }}>{t('b2b_companies', { defaultValue: 'Empresas B2B' })}</div>
            <div style={{ fontSize: 26, fontWeight: 700 }}>{totalCompanies}</div>
          </div>
          <div style={{ background: '#333333', color: '#fff', borderRadius: 12, padding: '14px 20px', minWidth: 200 }}>
            <div style={{ fontSize: 13, opacity: 0.9 }}>{t('b2b_services_month', { defaultValue: 'Servicios del mes' })}</div>
            <div style={{ fontSize: 26, fontWeight: 700 }}>{totalBookings}</div>
          </div>
          <div style={{ background: '#06C167', color: '#fff', borderRadius: 12, padding: '14px 20px', minWidth: 200 }}>
            <div style={{ fontSize: 13, opacity: 0.9 }}>{t('b2b_billed_month', { defaultValue: 'Facturado del mes' })}</div>
            <div style={{ fontSize: 26, fontWeight: 700 }}>{symbolize(totalBilled)}</div>
          </div>
          <TextField
            label={t('month', { defaultValue: 'Mes' })}
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            InputLabelProps={{ shrink: true, style: { fontFamily: FONT_FAMILY } }}
            style={{ minWidth: 160 }}
          />
        </div>
        <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 13, color: colors.SHADOW, marginBottom: 8 }}>
          {t('b2b_hint', { defaultValue: 'Cada empresa (aseguradora/flota/taller) accede con su cuenta de socio para solicitar y seguir sus servicios. Aquí consolidas su volumen y exportas el estado de cuenta para facturar.' })}
        </Typography>
        <MaterialTable
          title={t('b2b_partners', { defaultValue: 'Empresas B2B' })}
          columns={columns}
          style={{
            direction: isRTL === "rtl" ? "rtl" : "ltr",
            borderRadius: "8px",
            boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
            padding: "20px",
          }}
          data={data}
          options={{
            exportButton: false,
            ...TableStyle(settings)
          }}
          localization={localization(t)}
          actions={[
            rowData => ({
              icon: 'receipt_long',
              tooltip: t('b2b_export_statement', { defaultValue: 'Estado de cuenta (CSV)' }),
              disabled: !rowData._rows || rowData._rows.length === 0,
              onClick: (event, rowData) => exportStatement(rowData)
            })
          ]}
        />
      </ThemeProvider>
  );
}

export default B2BPartners;
